import { Box, Flex, Text } from '@chakra-ui/react'
import { GameRules } from 'types/types'
import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown/CurrencyDropdown'
import { AmountButtons } from '@/components/atoms/AmountButtons/AmountButtons'
import { StyledInput2 } from '@/components/atoms/StyledInput/StyledInput'
import { getTokenByDenom } from '@/services/token-service'
import { useEffect } from 'react'
import { useBalanceStore } from '@/context/UserBalance'
import { currenciesSettings, PlinkoState, usePlinkoStore } from '@/context/PlinkoProvider'
import { useTranslation } from 'next-i18next/pages'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'

export const PlinkoForm = () => {
  const { t } = useTranslation('common')
  const denom = useBalanceStore((state) => state.denom)
  const isFrozen = usePlinkoStore((state) => state.state == PlinkoState.GAMING)
  const maxWin = usePlinkoStore((state) => state.maxWin)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const token = getTokenByDenom(denom)
  const isUsdc = token?.currency === SupportedCurrencies.USDC

  const maxWinText = isUsdc && !isUsd ? usdcToFiat(maxWin).toFixed(2) + ' ' + getSymbol() : maxWin + ' ' + token?.name

  useEffect(() => {
    usePlinkoStore.getState().setDenom(denom)
  }, [denom])

  return (
    <Box rounded={'20px'} mb={{ base: 4, lg: 2 }} bg={'ninja.600'}>
      <Flex
        flexDir={{ base: 'column', lg: 'row' }}
        gap={{ base: 2, lg: 6 }}
        p={4}
        alignItems={{ base: 'space-between', lg: 'center' }}
      >
        <Flex width={{ base: 'full', lg: 'auto' }} flexShrink={0} justifyContent={'space-between'}>
          <Flex minW={'140px'} h={{ base: 'auto', lg: '61px' }} flexDir={'column'} justifyContent={'space-between'}>
            <Text fontWeight={700}>{t('common.your-bet')}</Text>
            <CurrencyDropdown isDisabled={isFrozen} />
          </Flex>
          {/* <RulesButton game={GameRules.PLINKO} styles={{ display: { base: 'block', lg: 'none' } }} /> */}
        </Flex>
        <PlinkoStyledInput isDisabled={isFrozen} />
        <PlinkoAmountButtons isDisabled={isFrozen} />
        {/* <RulesButton game={GameRules.PLINKO} styles={{ display: { base: 'none', lg: 'block' } }} /> */}
      </Flex>
      <Box px={4} pb={2}>
        <GameInfo rtp="97%" maxWin={maxWinText} />
      </Box>
    </Box>
  )
}

const PlinkoStyledInput = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useTranslation('common')
  const amount = usePlinkoStore((state) => state.amount)
  const errors = usePlinkoStore((state) => state.errors)
  const isDirty = usePlinkoStore((state) => state.isDirty)
  const denom = usePlinkoStore((state) => state.denom)
  const setValues = usePlinkoStore((state) => state.setValues)
  const maxValue = usePlinkoStore((state) => state.maxValue)

  const token = getTokenByDenom(denom)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const isUsdc = getTokenByDenom(denom)?.currency === SupportedCurrencies.USDC

  const maxInfoText =
    isUsdc && !isUsd
      ? t('common.max') + ' ' + usdcToFiat(maxValue).toFixed(2) + ' ' + getSymbol()
      : t('common.max') + ' ' + maxValue + ' ' + token.name

  return (
    <StyledInput2
      title={t('common.amount')}
      placeholder={t('common.enter-amount')}
      icon={token.icon('18px')}
      setValue={(amount) => setValues({ amount: { value: amount } })}
      errors={errors}
      isDirty={isDirty}
      amount={amount}
      clearable={true}
      infoText={maxInfoText}
      isDisabled={isDisabled}
      denom={denom}
    />
  )
}

const PlinkoAmountButtons = ({ isDisabled }: { isDisabled: boolean }) => {
  const denom = usePlinkoStore((state) => state.denom)
  const amount = usePlinkoStore((state) => state.amount)
  const setValues = usePlinkoStore((state) => state.setValues)

  return (
    <Flex
      flexShrink={0}
      minW={{ base: 0, md: '360px' }}
      h={{ base: 'auto', lg: '61px' }}
      rounded={'xl'}
      alignItems={'center'}
      px={{ base: 0, lg: 4 }}
      border={{ base: 'none', lg: '1px solid' }}
      borderColor={{ base: 'none', lg: 'ninja.500' }}
      display={{ base: 'flex', lg: 'none', xl: 'flex' }}
    >
      <AmountButtons
        maxValue={currenciesSettings.maxValue[denom]}
        denom={denom}
        setAmount={(value) => setValues({ amount: { value } })}
        amount={amount}
        disabled={isDisabled}
      />
    </Flex>
  )
}
